import { debounce } from '@yp/common/async.ts';
import { checkPresent } from '@yp/common/core.ts';
import type { DomEvent } from '@yp/common/html.ts';
import { isPresent, isString } from '@yp/common/identity.ts';
import { type Suggester, nextTabIndex } from '@yp/common/schema/forms.ts';
import { Milliseconds } from '@yp/common/units.ts';

const suggestionAutoSelectFirst = false;
const suggesterDebounceDelay = Milliseconds(250);
const updateFrequency: 'keystroke' | 'blur' = 'keystroke'; // 'blur';

export class Suggestions<T> {
  readonly active: boolean;
  get candidates() {
    return this.candidates_;
  }
  get index() {
    return this.index_;
  }

  private candidates_: ReadonlyArray<readonly [string, T]> = $state.raw([]);
  private index_ = $state(suggestionAutoSelectFirst ? 0 : -1);

  constructor(
    suggester: Suggester<T> | undefined,
    private readonly fromField: (value: string) => T,
  ) {
    this.active = isPresent(suggester);
    const suggest = (value: string) => {
      void suggester?.(value).then((responses) => {
        this.candidates_ = responses;
        // this.candidates.length = 0;
        // this.candidates.push(...responses);
      });
    };
    this.suggest = debounce(suggest, {
      delay: suggesterDebounceDelay,
      onWait: () => this.clear(),
    });
  }

  readonly suggest: (value: string) => void;

  clear() {
    this.candidates_ = [];
    // this.candidates.length = 0;
    this.index_ = suggestionAutoSelectFirst ? 0 : -1;
  }

  use(index: number) {
    // log('use suggestion', { s });
    const selected = this.candidates_[index];
    const value = checkPresent(selected[1]); // ?? selected[0];
    this.clear();
    return value;
    // local[prop.k] = s as T[keyof T & string];
    // onEvent('update');
    // // onChange(s);
    // // setOpen(false);
  }

  onInput(
    e: { currentTarget: HTMLElement & { value: string } },
    onValue: (value: T, el: HTMLElement) => void,
  ) {
    const inputValue = e.currentTarget.value;
    if (this.active) {
      if (isString(inputValue)) {
        this.suggest(inputValue);
      }
    } else if (updateFrequency === 'keystroke') {
      onValue(this.fromField(inputValue), e.currentTarget);
    }
  }

  onKeyDown(e: DomEvent<HTMLElement, KeyboardEvent>, onValue: (value: T, el: HTMLElement) => void) {
    let focusDelta = 0;
    if (e.key === 'ArrowDown') {
      if (this.candidates_.length) {
        this.index_ = (this.index_ + 1) % this.candidates_.length;
      } else {
        focusDelta = 1;
      }
    } else if (e.key === 'ArrowUp') {
      if (this.index_ >= 0) {
        this.index_ = (this.index_ + this.candidates_.length - 1) % this.candidates_.length;
      } else {
        focusDelta = -1;
      }
    } else if (e.key === 'Enter') {
      if (this.index_ >= 0) {
        onValue(this.use(this.index_), e.currentTarget);
      } else {
        focusDelta = 1;
      }
    } else if (e.key === 'Escape') {
      if (this.candidates_.length) {
        this.clear();
      }
    } else {
      return;
    }
    if (focusDelta !== 0) {
      const next = nextTabIndex(e.currentTarget, focusDelta);
      next?.focus();
    }
    e.preventDefault();
  }

  onBlur(
    e: DomEvent<HTMLElement & { value: string }, FocusEvent>,
    onValue: (value: T, el: HTMLElement) => void,
  ) {
    this.clear();
    onValue(this.fromField(e.currentTarget.value), e.currentTarget);
  }
}
